import React, { useCallback, useEffect, useRef, useState } from "react";
import classNames from "classnames";
import { useTranslation } from "react-i18next";
import { Tooltip } from "reactstrap";

import { formatWallClockTime } from "../../../../helpers/agendaDateTime";
import type { AgendaGridBlock } from "../../../../helpers/agendaGridTypes";
import type { GridPosition } from "../../../../helpers/agendaDayGrid";
import { getDisplayableAppointmentNotes } from "../../../../helpers/appointmentNotesDisplay";
import {
  buildClientNotesTooltip,
  getClientNotesPinLine,
  hasClientOperationalNotes,
} from "../../../../helpers/clientNotes";
import {
  getServicePreferencesPreview,
  hasClientServicePreferences,
} from "../../../../helpers/clientServicePreferences";
import { canViewClinicalRecords } from "../../../../helpers/clinicalRecordAccess";
import type { Appointment } from "../../../../services/appointmentService";
import ClientNotesPreviewModal from "../../../../Components/Studio/ClientNotesPreviewModal";

const LONG_PRESS_MS = 480;
const HOVER_DELAY_MS = 350;

type AgendaGridEventCardProps = {
  block: AgendaGridBlock;
  position: GridPosition;
  isSelected: boolean;
  resolveStatusLabel: (status: string) => string;
  showProfessional?: boolean;
  onClick: () => void;
  onFillHealthRecord?: (appointment: Appointment) => void;
  onViewEvolution?: (appointment: Appointment) => void;
  onClearArrived?: (appointment: Appointment) => void;
  onDragStart: (event: React.DragEvent<HTMLButtonElement>) => void;
  onDragEnd: () => void;
};

const AgendaGridEventCard = ({
  block,
  position,
  isSelected,
  resolveStatusLabel,
  showProfessional = false,
  onClick,
  onFillHealthRecord,
  onViewEvolution,
  onClearArrived,
  onDragStart,
  onDragEnd,
}: AgendaGridEventCardProps) => {
  const { t } = useTranslation();
  const tooltipTargetId = `agenda-grid-event-${block.id}`;
  const buttonRef = useRef<HTMLButtonElement>(null);
  const hoverTimerRef = useRef<number | null>(null);
  const longPressTimerRef = useRef<number | null>(null);
  const [tooltipOpen, setTooltipOpen] = useState(false);
  const [clientNotesOpen, setClientNotesOpen] = useState(false);

  const appointment = block.appointment;
  const canShowClinicalGate =
    canViewClinicalRecords() &&
    Boolean(appointment.has_clinical_gate_alert) &&
    Boolean(appointment.client_id);
  const canShowNoHealth =
    canViewClinicalRecords() &&
    (typeof appointment.needs_health_record === "boolean"
      ? appointment.needs_health_record
      : (appointment.client?.health_records_count ?? 0) === 0) &&
    Boolean(appointment.client_id);
  const canShowNeedsConsent =
    canViewClinicalRecords() && Boolean(appointment.needs_consent) && Boolean(appointment.client_id);
  const canShowPhotosExpiring =
    canViewClinicalRecords() &&
    Boolean(appointment.evolution_photos_expiring_soon) &&
    Boolean(appointment.client_id);
  const photosExpireInDays = appointment.evolution_photos_expire_in_days;
  const photosExpiringHint =
    photosExpireInDays === 0
      ? t("studio.agenda.badges.evolutionPhotosExpireTodayHint")
      : t("studio.agenda.badges.evolutionPhotosExpiringHint", {
          days: photosExpireInDays ?? 0,
        });
  const canShowPreattendOk =
    canViewClinicalRecords() &&
    Boolean(appointment.client_id) &&
    !canShowNoHealth &&
    !canShowNeedsConsent;
  const canShowPreattendPendingArrival =
    canViewClinicalRecords() &&
    Boolean(appointment.client_arrived_at) &&
    (canShowNoHealth || canShowNeedsConsent);
  const clientName = appointment.client?.name ?? t("studio.agenda.common.notAvailable");
  const serviceName = appointment.service?.name ?? t("studio.agenda.common.notAvailable");
  const professionalName =
    appointment.professional?.name ?? t("studio.agenda.common.notAvailable");
  const statusLabel = resolveStatusLabel(appointment.status);
  const timeRange = t("studio.agenda.fields.slot.option", {
    start: formatWallClockTime(appointment.start_datetime),
    end: formatWallClockTime(appointment.end_datetime),
  });
  const depositNote = appointment.deposit_collection_note?.trim();
  const appointmentNotes = getDisplayableAppointmentNotes(appointment.notes);
  const clientNotes = appointment.client?.notes?.trim() ?? "";
  const hasClientNotes = hasClientOperationalNotes(clientNotes);
  const clientNotesPin = getClientNotesPinLine(clientNotes, 40);
  const clientPrefs = appointment.client?.service_preferences ?? null;
  const hasClientPrefs = hasClientServicePreferences(clientPrefs);
  const clientPrefsPreview = getServicePreferencesPreview(clientPrefs, 2, 40);
  const extraServices = appointment.extra_services ?? [];
  const orderedExtras = [...extraServices].sort(
    (left, right) => (left.sort_order ?? 0) - (right.sort_order ?? 0)
  );
  const orderedExtraLabels = orderedExtras.map((item, index) =>
    t("studio.agenda.detail.extraServicesOrdered", {
      position: index + 1,
      name: item.name ?? `#${item.id}`,
    })
  );
  const orderedExtraNamesJoined = orderedExtraLabels.join(" · ");

  const clearTimers = useCallback(() => {
    if (hoverTimerRef.current !== null) {
      window.clearTimeout(hoverTimerRef.current);
      hoverTimerRef.current = null;
    }

    if (longPressTimerRef.current !== null) {
      window.clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
  }, []);

  useEffect(() => () => clearTimers(), [clearTimers]);

  const openTooltip = useCallback(() => setTooltipOpen(true), []);
  const closeTooltip = useCallback(() => setTooltipOpen(false), []);

  const handleMouseEnter = () => {
    clearTimers();
    hoverTimerRef.current = window.setTimeout(openTooltip, HOVER_DELAY_MS);
  };

  const handleMouseLeave = () => {
    clearTimers();
    closeTooltip();
  };

  const handleTouchStart = () => {
    clearTimers();
    longPressTimerRef.current = window.setTimeout(openTooltip, LONG_PRESS_MS);
  };

  const handleTouchEnd = () => {
    clearTimers();
  };

  return (
    <>
      <button
        ref={buttonRef}
        id={tooltipTargetId}
        type="button"
        className={classNames("agenda-grid-event", block.classNames, {
          "agenda-grid-event--selected": isSelected,
          "agenda-grid-event--draggable": block.draggable,
          "agenda-grid-event--arrived": Boolean(appointment.client_arrived_at),
        })}
        style={{
          top: `${position.topPercent}%`,
          height: `${position.heightPercent}%`,
          backgroundColor: block.color,
          borderColor: block.borderColor,
        }}
        draggable={block.draggable}
        onDragStart={onDragStart}
        onDragEnd={onDragEnd}
        onClick={(event) => {
          event.stopPropagation();
          closeTooltip();
          onClick();
        }}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
        onTouchCancel={handleTouchEnd}
        aria-describedby={tooltipOpen ? `${tooltipTargetId}-tip` : undefined}
      >
        <span className="agenda-grid-event__time">
          {formatWallClockTime(block.startDatetime)}
          {appointment.has_package_credit ? (
            <i
              className="ri-coupon-2-line ms-1"
              aria-hidden="true"
              title={t("studio.agenda.badges.packageCredit", {
                count: appointment.package_credit_sessions ?? 0,
              })}
            />
          ) : null}
          {appointment.has_active_club ? (
            <i
              className="ri-vip-crown-line ms-1"
              aria-hidden="true"
              title={
                appointment.club_visits_remaining != null
                  ? t("studio.agenda.badges.clubMemberRemaining", {
                      count: appointment.club_visits_remaining,
                    })
                  : t("studio.agenda.badges.clubMember")
              }
            />
          ) : null}
          {orderedExtras.length > 0 ? (
            <i
              className="ri-add-circle-line ms-1"
              aria-hidden="true"
              title={t("studio.agenda.calendar.extrasTooltip", {
                count: orderedExtras.length,
                names: orderedExtraNamesJoined,
              })}
            />
          ) : null}
          {canShowNoHealth ? (
            <span
              role="button"
              tabIndex={0}
              className="agenda-grid-event__no-health ms-1 text-warning"
              style={{ cursor: "pointer", display: "inline-flex" }}
              title={t("studio.agenda.badges.noHealthRecordHint")}
              aria-label={t("studio.agenda.badges.noHealthRecordHint")}
              onClick={(event) => {
                event.stopPropagation();
                event.preventDefault();
                closeTooltip();
                onFillHealthRecord?.(appointment);
              }}
              onMouseDown={(event) => {
                event.stopPropagation();
              }}
              onKeyDown={(event) => {
                if (event.key !== "Enter" && event.key !== " ") {
                  return;
                }
                event.stopPropagation();
                event.preventDefault();
                closeTooltip();
                onFillHealthRecord?.(appointment);
              }}
            >
              <i className="ri-heart-add-line" aria-hidden="true" />
            </span>
          ) : null}
          {canShowClinicalGate ? (
            <span
              role="button"
              tabIndex={0}
              className="agenda-grid-event__clinical-gate ms-1 text-danger"
              style={{ cursor: "pointer", display: "inline-flex" }}
              title={t("studio.agenda.badges.clinicalGateHint")}
              aria-label={t("studio.agenda.badges.clinicalGateHint")}
              onClick={(event) => {
                event.stopPropagation();
                event.preventDefault();
                closeTooltip();
                onFillHealthRecord?.(appointment);
              }}
              onMouseDown={(event) => {
                event.stopPropagation();
              }}
              onKeyDown={(event) => {
                if (event.key !== "Enter" && event.key !== " ") {
                  return;
                }
                event.stopPropagation();
                event.preventDefault();
                closeTooltip();
                onFillHealthRecord?.(appointment);
              }}
            >
              <i className="ri-alert-line" aria-hidden="true" />
            </span>
          ) : null}
          {canShowPhotosExpiring ? (
            <span
              role="button"
              tabIndex={0}
              className="agenda-grid-event__photos-expiring ms-1 text-warning"
              style={{ cursor: "pointer", display: "inline-flex" }}
              title={photosExpiringHint}
              aria-label={photosExpiringHint}
              onClick={(event) => {
                event.stopPropagation();
                event.preventDefault();
                closeTooltip();
                onViewEvolution?.(appointment);
              }}
              onMouseDown={(event) => {
                event.stopPropagation();
              }}
              onKeyDown={(event) => {
                if (event.key !== "Enter" && event.key !== " ") {
                  return;
                }
                event.stopPropagation();
                event.preventDefault();
                closeTooltip();
                onViewEvolution?.(appointment);
              }}
            >
              <i className="ri-timer-line" aria-hidden="true" />
            </span>
          ) : null}
          {canShowNeedsConsent ? (
            <span
              role="button"
              tabIndex={0}
              className="agenda-grid-event__needs-consent ms-1 text-danger"
              style={{ cursor: "pointer", display: "inline-flex" }}
              title={t("studio.agenda.badges.needsConsentHint")}
              aria-label={t("studio.agenda.badges.needsConsentHint")}
              onClick={(event) => {
                event.stopPropagation();
                event.preventDefault();
                closeTooltip();
                onFillHealthRecord?.(appointment);
              }}
              onMouseDown={(event) => {
                event.stopPropagation();
              }}
              onKeyDown={(event) => {
                if (event.key !== "Enter" && event.key !== " ") {
                  return;
                }
                event.stopPropagation();
                event.preventDefault();
                closeTooltip();
                onFillHealthRecord?.(appointment);
              }}
            >
              <i className="ri-quill-pen-line" aria-hidden="true" />
            </span>
          ) : null}
          {canShowPreattendPendingArrival ? (
            <span
              role="button"
              tabIndex={0}
              className="agenda-grid-event__preattend-pending ms-1 text-warning"
              style={{ cursor: "pointer", display: "inline-flex" }}
              title={t("studio.agenda.badges.preattendPendingArrivalHint")}
              aria-label={t("studio.agenda.badges.preattendPendingArrivalHint")}
              onClick={(event) => {
                event.stopPropagation();
                event.preventDefault();
                closeTooltip();
                onFillHealthRecord?.(appointment);
              }}
              onMouseDown={(event) => {
                event.stopPropagation();
              }}
              onKeyDown={(event) => {
                if (event.key !== "Enter" && event.key !== " ") {
                  return;
                }
                event.stopPropagation();
                event.preventDefault();
                closeTooltip();
                onFillHealthRecord?.(appointment);
              }}
            >
              <i className="ri-alarm-warning-line" aria-hidden="true" />
            </span>
          ) : null}
          {canShowPreattendOk ? (
            <span
              className="agenda-grid-event__preattend-ok ms-1 text-success"
              style={{ display: "inline-flex" }}
              title={t("studio.agenda.badges.preattendOkHint")}
              aria-label={t("studio.agenda.badges.preattendOkHint")}
            >
              <i className="ri-checkbox-circle-line" aria-hidden="true" />
            </span>
          ) : null}
          {appointment.client_arrived_at ? (
            onClearArrived ? (
              <span
                role="button"
                tabIndex={0}
                className="agenda-grid-event__arrived ms-1 text-success"
                style={{ cursor: "pointer", display: "inline-flex" }}
                title={t("studio.agenda.badges.arrivedHint")}
                aria-label={t("studio.agenda.badges.arrivedHint")}
                onClick={(event) => {
                  event.stopPropagation();
                  event.preventDefault();
                  closeTooltip();
                  onClearArrived(appointment);
                }}
                onMouseDown={(event) => {
                  event.stopPropagation();
                }}
                onKeyDown={(event) => {
                  if (event.key !== "Enter" && event.key !== " ") {
                    return;
                  }
                  event.stopPropagation();
                  event.preventDefault();
                  closeTooltip();
                  onClearArrived(appointment);
                }}
              >
                <i className="ri-walk-line" aria-hidden="true" />
              </span>
            ) : (
              <i
                className="ri-walk-line ms-1 text-success"
                aria-hidden="true"
                title={t("studio.agenda.badges.arrived")}
              />
            )
          ) : null}
          {appointmentNotes ? (
            <i
              className="ri-sticky-note-line ms-1"
              aria-hidden="true"
              title={appointmentNotes}
            />
          ) : null}
          {hasClientNotes ? (
            <i
              className="ri-user-voice-line ms-1 text-warning"
              aria-hidden="true"
              title={buildClientNotesTooltip(clientNotes, (preview) =>
                t("clients.picker.notesTooltip", { preview })
              )}
              role="button"
              tabIndex={0}
              onClick={(event) => {
                event.stopPropagation();
                event.preventDefault();
                closeTooltip();
                setClientNotesOpen(true);
              }}
              onKeyDown={(event) => {
                if (event.key !== "Enter" && event.key !== " ") {
                  return;
                }
                event.stopPropagation();
                event.preventDefault();
                closeTooltip();
                setClientNotesOpen(true);
              }}
              aria-label={t("clients.picker.viewNotesAria")}
            />
          ) : null}
          {hasClientPrefs ? (
            <i
              className="ri-sparkling-2-line ms-1 text-primary"
              aria-hidden="true"
              title={t("clients.picker.preferencesTooltip", {
                preview:
                  clientPrefsPreview ?? t("clients.preferences.chipFallback"),
              })}
              aria-label={t("clients.picker.viewPreferencesAria")}
            />
          ) : null}
        </span>
        <span className="agenda-grid-event__title">{block.title}</span>
        {hasClientNotes && clientNotesPin ? (
          <span
            className="agenda-grid-event__notes agenda-grid-event__notes--client"
            title={buildClientNotesTooltip(clientNotes, (preview) =>
              t("clients.picker.notesTooltip", { preview })
            )}
            data-clarity-mask="true"
          >
            {clientNotesPin}
          </span>
        ) : hasClientPrefs && clientPrefsPreview && clientPrefsPreview !== "★" ? (
          <span
            className="agenda-grid-event__notes agenda-grid-event__notes--prefs"
            title={t("clients.picker.preferencesTooltip", {
              preview: clientPrefsPreview,
            })}
            data-clarity-mask="true"
          >
            {clientPrefsPreview}
          </span>
        ) : appointmentNotes ? (
          <span className="agenda-grid-event__notes" title={appointmentNotes}>
            {appointmentNotes.length > 40
              ? `${appointmentNotes.slice(0, 40).trimEnd()}…`
              : appointmentNotes}
          </span>
        ) : null}
      </button>
      <Tooltip
        isOpen={tooltipOpen}
        target={tooltipTargetId}
        toggle={closeTooltip}
        placement="top"
        fade={false}
        className="agenda-grid-event-tooltip"
        innerClassName="agenda-grid-event-tooltip__inner"
      >
        <div id={`${tooltipTargetId}-tip`} className="agenda-grid-event-tooltip__content">
          <div className="agenda-grid-event-tooltip__client">{clientName}</div>
          <div className="agenda-grid-event-tooltip__service">{serviceName}</div>
          {orderedExtraLabels.length > 0 ? (
            <div className="agenda-grid-event-tooltip__meta">
              {t("studio.agenda.calendar.extrasTooltip", {
                count: orderedExtraLabels.length,
                names: orderedExtraNamesJoined,
              })}
            </div>
          ) : null}
          {hasClientNotes ? (
            <div className="agenda-grid-event-tooltip__note" data-clarity-mask="true">
              {t("clients.picker.notesTooltip", {
                preview: clientNotesPin ?? clientNotes.slice(0, 120),
              })}
            </div>
          ) : null}
          {hasClientPrefs ? (
            <div className="agenda-grid-event-tooltip__note text-primary" data-clarity-mask="true">
              {t("clients.picker.preferencesTooltip", {
                preview:
                  clientPrefsPreview ?? t("clients.preferences.chipFallback"),
              })}
            </div>
          ) : null}
          {appointmentNotes ? (
            <div className="agenda-grid-event-tooltip__note">
              {t("studio.agenda.detail.notes")}: {appointmentNotes}
            </div>
          ) : null}
          {appointment.has_package_credit ? (
            <div className="agenda-grid-event-tooltip__meta text-success">
              {t("studio.agenda.badges.packageCredit", {
                count: appointment.package_credit_sessions ?? 0,
              })}
            </div>
          ) : null}
          {appointment.has_active_club ? (
            <div className="agenda-grid-event-tooltip__meta text-primary">
              {appointment.club_visits_remaining != null
                ? t("studio.agenda.badges.clubMemberRemaining", {
                    count: appointment.club_visits_remaining,
                  })
                : t("studio.agenda.badges.clubMember")}
            </div>
          ) : null}
          {canShowNoHealth ? (
            <div className="agenda-grid-event-tooltip__meta text-warning">
              {t("studio.agenda.badges.noHealthRecord")}
            </div>
          ) : null}
          {canShowNeedsConsent ? (
            <div className="agenda-grid-event-tooltip__meta text-danger">
              {t("studio.agenda.badges.needsConsent")}
            </div>
          ) : null}
          {canShowPhotosExpiring ? (
            <div className="agenda-grid-event-tooltip__meta text-warning">
              {photosExpireInDays === 0
                ? t("studio.agenda.badges.evolutionPhotosExpireToday")
                : t("studio.agenda.badges.evolutionPhotosExpiring", {
                    days: photosExpireInDays ?? 0,
                  })}
            </div>
          ) : null}
          {appointment.client_arrived_at ? (
            <div className="agenda-grid-event-tooltip__meta text-success">
              {t("studio.agenda.badges.arrived")}
            </div>
          ) : null}
          <div className="agenda-grid-event-tooltip__meta">
            <span className="badge bg-light text-body">{statusLabel}</span>
            <span className="agenda-grid-event-tooltip__time">{timeRange}</span>
          </div>
          {showProfessional ? (
            <div className="agenda-grid-event-tooltip__professional">
              {t("studio.agenda.detail.professional")}: {professionalName}
            </div>
          ) : null}
          {depositNote ? (
            <div className="agenda-grid-event-tooltip__note">
              {t("studio.agenda.calendar.eventTooltipNote")}: {depositNote}
            </div>
          ) : null}
        </div>
      </Tooltip>
      <ClientNotesPreviewModal
        isOpen={clientNotesOpen}
        notes={clientNotes}
        clientName={clientName}
        onClose={() => setClientNotesOpen(false)}
      />
    </>
  );
};

export default AgendaGridEventCard;
